import FormHelperText from '@material-ui/core/FormHelperText'
import { styled } from 'opensolar-ui'
import { InputProps, Validator } from 'ra-core'
import { InputHelperText } from 'ra-ui-materialui'
import { useTranslate } from 'react-admin'
import { FileRecord, ResourcePrefix, useFileUploadContext } from '../controller/UploadFileContext'
import { useSyncUploadFileToField } from '../hooks/useSyncUploadFileToField'
import { UploadFileFormat } from '../types'
import { formatFileSize } from '../utils'
import { UploadFileDropzone } from './UploadFileDropzone'
import { UploadFileGrid } from './UploadFileGrid'
import { UploadFileList } from './UploadFileList'
import { UploadFileTags } from './UploadFileTags'

type UploadFileInputProps = InputProps & {
  resource_prefix: ResourcePrefix
  fileTypesAccepted?: string
  placeholderIcon?: React.ReactNode
  multiple?: boolean
  fileTypeLabel?: string
  showTags?: boolean
  fullWidth?: boolean
  viewType?: 'list' | 'grid'
  hideDropzoneWhenPopulated?: boolean
  maxFileSizeBytes?: number
  autoUpload?: boolean
  formFormat?: UploadFileFormat
  onUploadSuccess?: (uploadedFiles: FileRecord[]) => void
  onFileRemove?: (record: FileRecord) => void
  helperText?: string
  showFileSizeHelperText?: boolean
  validate?: Validator | Validator[]
  format?: (value: any) => any
  parse?: (value: any) => any
  compact?: boolean
  showRemoveButton?: boolean
}

/**
 * React-admin input component that wraps UploadFileDropzone, UploadFileList, and UploadFileTags.
 * Uses useSyncUploadFileToField hook to sync form field with UploadFileContext.
 */
export const UploadFileInput = ({
  source,
  resource_prefix,
  fileTypesAccepted = '',
  placeholderIcon,
  multiple = false,
  fileTypeLabel,
  showTags = true,
  fullWidth = false,
  viewType = 'list',
  hideDropzoneWhenPopulated = false,
  maxFileSizeBytes,
  autoUpload = false,
  formFormat = 'id',
  onUploadSuccess,
  onFileRemove,
  validate,
  helperText,
  showFileSizeHelperText = false,
  format,
  parse,
  compact = false,
  showRemoveButton = true,
  ...rest
}: UploadFileInputProps) => {
  // Use the RA source as the UploadFile slot
  const translate = useTranslate()
  const slot = source
  const { uploadStatus } = useFileUploadContext()

  const effectiveHelperText =
    showFileSizeHelperText && maxFileSizeBytes != null
      ? translate('Tip: Max image size is %{maxSize}', {
          maxSize: formatFileSize(maxFileSizeBytes, translate),
        })
      : helperText

  // Use hook to sync form field with file records in context
  const {
    meta: { touched, error },
  } = useSyncUploadFileToField(source, slot, resource_prefix, multiple, formFormat, validate, format, parse)

  const FileViewComponent = viewType === 'grid' ? UploadFileGrid : UploadFileList

  const dropzone = (
    <UploadFileDropzone
      slot={slot}
      uploadStatus={uploadStatus}
      fileTypesAccepted={fileTypesAccepted}
      placeholderIcon={placeholderIcon}
      multiple={multiple}
      fileTypeLabel={fileTypeLabel}
      resource_prefix={resource_prefix}
      hideWhenPopulated={hideDropzoneWhenPopulated}
      maxFileSizeBytes={maxFileSizeBytes}
      autoUpload={autoUpload}
      onUploadSuccess={onUploadSuccess}
      compact={compact}
    />
  )

  const fileViewProps = {
    slot,
    resource_prefix,
    uploadStatus,
    dropzone,
    onFileRemove,
    ...(viewType === 'list' ? { compact, showRemoveButton } : {}),
  }

  return (
    <UploadFileInputWrapper fullWidth={fullWidth} compact={compact}>
      <FileViewComponent {...fileViewProps} />
      {(effectiveHelperText || (touched && error)) && (
        <FormHelperText error={!!(touched && error)}>
          <InputHelperText touched={!!touched} error={error} helperText={effectiveHelperText} />
        </FormHelperText>
      )}
      {showTags && <UploadFileTags resource_prefix={resource_prefix} slot={slot} />}
    </UploadFileInputWrapper>
  )
}

const UploadFileInputWrapper = styled('div', {
  shouldForwardProp: (prop) => prop !== 'fullWidth' && prop !== 'compact',
})<{ fullWidth?: boolean; compact?: boolean }>(({ fullWidth, compact }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: compact ? 4 : 8,
  alignItems: 'stretch',
  width: fullWidth ? '100%' : 'unset',
  maxWidth: fullWidth ? 'unset' : 460,
}))
